import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isTrtProduct } from '../../utils/trtProducts';
import {
  ASYNC_REVIEW_NOTICE,
  APPROVAL_UPDATE_NOTICE,
  CONTACT_IF_QUESTIONS_NOTICE,
  NO_APPOINTMENT_NEEDED_NOTICE
} from '../../utils/asyncConsultCopy';
import { customerDosageText } from '../../utils/doctorNowDosageSummary';
import styles from './CheckoutSuccessPage.module.css';

const CheckoutSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Sandbox (Test Admin Dashboard) is SERVER-SIDE state: `users.sandbox_mode`, surfaced as the
  // top-level `sandboxMode` on /auth/me and held on the auth context — never read from localStorage,
  // which a client could forge. In sandbox every outbound email is suppressed at the single funnel in
  // emailService.sendEmail (`resolveSandbox`), so the two email sentences below would otherwise lie.
  const { sandboxMode } = useAuth();
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    const state = location.state;
    if (state?.order) {
      const order = state.order;
      // If consultationFee not in pricing (old state), check holdStatus
      if (order.holdStatus === 'held' && order.consultationHoldTransactionId && (!order.pricing?.consultationFee)) {
        // Tier member with consultation hold — show $30 default consult fee
        if (!order.pricing) order.pricing = {};
        order.pricing.consultationFee = order.pricing.consultationFee || 3000;
      }
      setOrderData(order);
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [location, navigate]);

  const formatPrice = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };


  if (!orderData) return null;

  // ── Card #54418: the consultation fee is shown ONCE and counted ONCE. ──
  // `pricing.total` is the engine total DUE NOW (after account credit). A CAPTURED fee is
  // already INSIDE it, so the pre-#54418 `total + consultationFee` displayed the fee twice
  // (S3 §3.3). A HELD fee sits OUTSIDE the engine total by design — it is a contingent line
  // charged only if the prescription is denied — so it is shown as that line and is deliberately
  // NOT added to the payable total. `holdStatus` is the server's own stamp for which shape this
  // order has: 'held' means the fee is outside `total`, anything else means it is inside.
  const consultationFeeCents = orderData.pricing?.consultationFee || 0;
  const totalCents = orderData.pricing?.total ?? orderData.total ?? 0;
  const feeInsideTotal = consultationFeeCents > 0 && orderData.holdStatus !== 'held';
  const productPriceCents = feeInsideTotal
    ? Math.max(0, totalCents - consultationFeeCents)
    : totalCents;

  const isDoctorNow = orderData.providerType === 'Doctor Now';
  // Project Testosterone (TRT). ⚠️ NOT derived from providerType: Cream and
  // Cypionate deliberately stay on `provider_type = 'DrCare'` until launch
  // approval (board safety rule 4), so a TRT order would otherwise fall into the
  // DrCare branch below and be promised an ASYNCHRONOUS review it never gets —
  // which is the wrong workflow entirely, because DoctorNow coordinates a video
  // visit for TRT. `submitCheckout` ships no slug, so this matches on the product
  // name; see web/utils/trtProducts.js.
  const isTrt = isTrtProduct(orderData.product);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Logo */}
        <div className={styles.logoRow}>
          <img loading="lazy" src="/oval-logo.webp" alt="OVAL" className={styles.logo} />
        </div>

        {/* Success Header - checkmark inline with text */}
        <div className={styles.header}>
          <div className={styles.headerInline}>
            <div className={styles.checkmark}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <h1 className={styles.title}>Thank You for Your Order</h1>
              <p className={styles.subtitle}>Your request has been submitted successfully</p>
            </div>
          </div>
        </div>

        {/* TRT: DoctorNow coordinates a VIDEO consultation.
            ⛔ No phone-call language, no Oval scheduling CTA, no appointment time.
            TESTOSTERONE-LAUNCH-PLAN.md §5.7 records a contradiction — product config
            says `consult_type: Async` while the encounter schema says Video Call. The
            copy describes a VIDEO consultation and never promises an async review; the
            config field itself is left untouched. §6 Q1 (does the vendor own video
            coordination?) is unconfirmed in writing, so this is the interim, reversible
            position the current evidence supports. */}
        {isTrt && (
          <div className={styles.doctorNowCard}>
            <div className={styles.cardHeaderInline}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" style={{flexShrink: 0}}>
                <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="10" />
              </svg>
              <h3 className={styles.cardTitle}>Your request has been submitted for provider review</h3>
            </div>
            <p className={styles.cardText}>
              <strong>DoctorNow coordinates your video consultation.</strong> You do not need to
              book a time here &mdash; they will reach out with the details for your visit.
            </p>
            <p className={styles.cardText}>
              A prescription can only be written after a licensed provider has reviewed your
              intake and your lab results and completed that video consultation.
            </p>
            <p className={styles.cardText}>
              {sandboxMode
                ? 'Test mode: no emails were sent for this order. On a real order you would be notified as each step is completed.'
                : 'We will notify you as each step is completed.'}
            </p>
          </div>
        )}

        {/* DoctorNow: simplified success card */}
        {isDoctorNow && !isTrt && (
          <div className={styles.doctorNowCard}>
            <div className={styles.cardHeaderInline}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" style={{flexShrink: 0}}>
                <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="10" />
              </svg>
              <h3 className={styles.cardTitle}>Thank you! Your submission has been submitted for approval.</h3>
            </div>
            <p className={styles.cardText}>
              We will let you know through email or text about the next steps.
            </p>
          </div>
        )}

        {/* Box 1: Asynchronous review status (GREEN) - DrCare only.
            DrCare is an ASYNCHRONOUS consultation: the member submits an intake, a
            licensed physician decides online, and the outcome arrives as a status
            update. Nothing is asked of the member here, so this block carries no
            number and no booking CTA — the red "act now" treatment it replaced was
            the single loudest instruction to attend a routine call.
            ⚠️ Every sentence is IMPORTED from web/utils/asyncConsultCopy.js, the one
            place this wording lives. ⛔ Do not retype it — four surfaces render it and
            it drifted before that module existed. */}
        {!isDoctorNow && !isTrt && (
        <div className={styles.reviewCard}>
          <div className={styles.cardHeaderInline}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" style={{flexShrink: 0}} aria-hidden="true" focusable="false">
              <path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h3 className={styles.cardTitle}>Your intake is with a physician for review</h3>
          </div>
          <p className={styles.reviewHighlight}>{NO_APPOINTMENT_NEEDED_NOTICE}</p>
          <p className={styles.cardText}>{ASYNC_REVIEW_NOTICE}</p>
          <p className={styles.cardText}>{APPROVAL_UPDATE_NOTICE}</p>
          <p className={styles.cardText}>{CONTACT_IF_QUESTIONS_NOTICE}</p>
        </div>
        )}

        {/* Box 2: Awaiting Approval - DrCare only */}
        {!isDoctorNow && !isTrt && (
        <div className={styles.statusCard}>
          <div className={styles.cardHeaderInline}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" style={{flexShrink: 0}}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3 className={styles.cardTitle}>Your Order is Awaiting Approval</h3>
          </div>
          {/* ⚠️ The clause promising that the reviewer would make contact within the
              window was removed: it named contact as the EXPECTED next step, which is
              the affordance this page no longer has. The 3-business-day figure is kept
              — it is the REVIEW turnaround, not a callback window. The reviewer noun is
              deliberately left as-is; who performs the review is a clinical claim this
              card did not set out to change.
              ⛔ This comment must NOT quote the deleted wording verbatim — the absence
              tests read the shipped source, and a JSX comment is not stripped by the
              line-based stripComments() they use. ROLLBACK is recorded on card 40754. */}
          <p className={styles.cardText}>
            Your order is currently on hold pending medical review. A licensed pharmacist
            will review your intake information and post a decision, usually within
            <strong> 3 business days</strong>.
          </p>
          <p className={styles.cardText}>
            {sandboxMode
              ? 'Test mode: no emails were sent for this order. On a real order you would receive an email confirmation once it has been approved or if any additional information is needed.'
              : 'You will receive an email confirmation once your order has been approved or if any additional information is needed.'}
          </p>
        </div>
        )}

        {/* Box 3: Order Details */}
        <div className={styles.orderCard}>
          <div className={styles.orderRow}>
            <span className={styles.label}>Order Number</span>
            <span className={styles.value}>{orderData.orderNumber}</span>
          </div>
          {orderData.product && (
            <div className={styles.orderRow}>
              <span className={styles.label}>Product</span>
              <span className={styles.value}>{orderData.product.name}</span>
            </div>
          )}
          {/* Card 54406: a Doctor Now dose is set by the clinician, so the internal default
              this order was priced with is never shown as the customer's dose. */}
          {orderData.dosage && (
            <div className={styles.orderRow}>
              <span className={styles.label}>Dosage</span>
              <span className={styles.value}>{customerDosageText({ isDoctorNow, amount: orderData.dosage.amount })}</span>
            </div>
          )}
          <div className={styles.orderRow}>
            <span className={styles.label}>Product Price</span>
            <span className={styles.value}>{formatPrice(productPriceCents)}</span>
          </div>
          {consultationFeeCents > 0 && (
            <div className={styles.consultRow}>
              <span className={styles.label}>Consultation Fee</span>
              <div className={styles.consultValue}>
                {/* 'pending' is TRUE only for a hold. A captured fee is already collected —
                    the server answered `payment.status: authorized` for it. */}
                {orderData.holdStatus === 'held' && <span className={styles.pendingBadge}>pending</span>}
                <span className={styles.value}>{formatPrice(consultationFeeCents)}</span>
              </div>
            </div>
          )}
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Total</span>
            <span className={styles.valueTotal}>{formatPrice(totalCents)}</span>
          </div>
        </div>

        {/* Box 4: What's Next - DrCare only */}
        {!isDoctorNow && !isTrt && (
        <div className={styles.nextSteps}>
          <h3 className={styles.nextTitle}>What Happens Next</h3>
          <div className={styles.stepsList}>
            <div className={styles.stepItem}>
              <span className={styles.stepNumber}>1</span>
              <div>
                <strong>Medical Review</strong>
                <p>A pharmacist reviews your intake form and ID verification</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <span className={styles.stepNumber}>2</span>
              <div>
                {/* Step 2 is a STATUS UPDATE, not a call. ⛔ Do not restate the old
                    heading or its callback sentence here — the absence tests grep this
                    source and JSX comments survive stripComments(). */}
                <strong>Approval Status Update</strong>
                <p>Your approval or denial appears as a status update on your order, usually within 3 business days</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <span className={styles.stepNumber}>3</span>
              <div>
                <strong>Shipment</strong>
                <p>Once approved, your medication ships directly to your door</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Box 4: What's Next - TRT variant. Step 3 says "if the provider approves",
            never that approval is expected. */}
        {isTrt && (
        <div className={styles.nextSteps}>
          <h3 className={styles.nextTitle}>What Happens Next</h3>
          <div className={styles.stepsList}>
            <div className={styles.stepItem}>
              <span className={styles.stepNumber}>1</span>
              <div>
                <strong>Video Consultation</strong>
                <p>DoctorNow arranges your video visit with a licensed provider and reaches out with the details</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <span className={styles.stepNumber}>2</span>
              <div>
                <strong>Provider Review</strong>
                <p>The provider reviews your intake form and your lab results alongside the consultation</p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <span className={styles.stepNumber}>3</span>
              <div>
                <strong>Prescription Decision</strong>
                <p>If the provider approves treatment, your medication ships directly to your door</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Confirmation Note */}
        <div className={styles.confirmNote}>
          <p>
            {sandboxMode
              ? 'Test mode: no confirmation email was sent. Email delivery is disabled for sandbox orders.'
              : 'A confirmation email has been sent to your registered email address.'}
          </p>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button className={styles.primaryBtn} onClick={() => navigate('/orders')}>
            View My Orders
          </button>
          <button className={styles.secondaryBtn} onClick={() => navigate('/catalog')}>
            Back to Catalog
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
